import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  MapIcon,
  Squares2X2Icon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { Wrapper, Status } from '@googlemaps/react-wrapper';

import * as API from '../../api';
import EventCard from '../../shared/components/EventCard';
import EventsFilters from './EventsFilters';
import Map from '../MapViewer/Map';

/**
 * Premium /events (and /search) listing page.
 *
 * Reuses the home-page EventCard and design system. The data flow is
 * unchanged from the legacy page:
 *   - pathname "/search" → API.Search(params)
 *   - otherwise          → API.Events(params)
 * with the same query params (type, city, minPrice, maxPrice, activityName,
 * miles, eventType, page). Filters and search keep everything in the URL so
 * results are shareable and the back button works.
 */

const PER_PAGE = 12;

// --- URL <-> filter mapping -------------------------------------------------

function parseFilters(search) {
  const q = new URLSearchParams(search);
  const typeStr = q.get('type') || '';
  return {
    types: typeStr ? typeStr.split(',').filter(Boolean) : [],
    city: q.get('city') && q.get('city') !== 'all' ? q.get('city') : '',
    minPrice: q.get('minPrice') || '',
    maxPrice: q.get('maxPrice') || '',
  };
}

function buildQuery({ filters, activityName, miles, eventType, page }) {
  const q = new URLSearchParams();
  if (filters.types.length) q.set('type', filters.types.join(','));
  if (filters.city) q.set('city', filters.city);
  if (filters.minPrice) q.set('minPrice', filters.minPrice);
  if (filters.maxPrice) q.set('maxPrice', filters.maxPrice);
  if (activityName) q.set('activityName', activityName);
  if (miles) q.set('miles', miles);
  if (eventType) q.set('eventType', eventType);
  if (page && page > 1) q.set('page', String(page));
  return q.toString();
}

// --- small UI pieces --------------------------------------------------------

function CardSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-card">
      <div className="aspect-[4/3] animate-pulse bg-slate-100" />
      <div className="space-y-2 p-4">
        <div className="h-4 w-4/5 animate-pulse rounded bg-slate-100" />
        <div className="h-3 w-2/5 animate-pulse rounded bg-slate-100" />
        <div className="mt-3 h-6 w-2/5 animate-pulse rounded bg-slate-100" />
      </div>
    </div>
  );
}

function Pagination({ page, pages, onChange }) {
  if (pages <= 1) return null;
  // Compact window: first, last, current ±1, with ellipses.
  const items = [];
  const push = (n) => items.push(n);
  const window = new Set([1, pages, page, page - 1, page + 1]);
  let prev = 0;
  for (let n = 1; n <= pages; n += 1) {
    if (window.has(n)) {
      if (n - prev > 1) push('…');
      push(n);
      prev = n;
    }
  }

  return (
    <nav className="mt-10 flex items-center justify-center gap-1.5" aria-label="Pagination">
      <button
        type="button"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-ink transition-colors hover:border-brand-500 hover:text-brand-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-ink"
        aria-label="Previous"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 rtl:rotate-180" aria-hidden>
          <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 0 1-.02 1.06L8.832 10l3.938 3.71a.75.75 0 1 1-1.04 1.08l-4.5-4.25a.75.75 0 0 1 0-1.08l4.5-4.25a.75.75 0 0 1 1.06.02Z" clipRule="evenodd" />
        </svg>
      </button>

      {items.map((it, i) =>
        it === '…' ? (
          <span key={`e${i}`} className="px-1 text-ink-subtle">
            …
          </span>
        ) : (
          <button
            key={it}
            type="button"
            onClick={() => onChange(it)}
            aria-current={it === page ? 'page' : undefined}
            className={
              it === page
                ? 'flex h-10 w-10 items-center justify-center rounded-full bg-brand-500 text-sm font-semibold text-white shadow-card'
                : 'flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-sm font-medium text-ink transition-colors hover:border-brand-500 hover:text-brand-500'
            }
          >
            {it}
          </button>
        )
      )}

      <button
        type="button"
        onClick={() => onChange(Math.min(pages, page + 1))}
        disabled={page >= pages}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-ink transition-colors hover:border-brand-500 hover:text-brand-500 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:text-ink"
        aria-label="Next"
      >
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 rtl:rotate-180" aria-hidden>
          <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.168 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z" clipRule="evenodd" />
        </svg>
      </button>
    </nav>
  );
}

// --- map view ---------------------------------------------------------------

// Fallback view: the whole of Morocco.
const MAP_FALLBACK = { lat: 31.7917, lng: -7.0926 };

/**
 * Renders the CURRENT filtered results on a Google map, so the sidebar
 * filters drive the pins exactly as they drive the cards.
 *
 * The API returns lat/lng as strings and 15 of 152 events have none at all,
 * so coordinates are parsed and invalid ones dropped — otherwise the map
 * silently centres on (0,0) in the Atlantic.
 */
function EventsMap({ events }) {
  const { t } = useTranslation();

  const points = React.useMemo(
    () =>
      (events || [])
        .map((e) => ({ ...e, lat: parseFloat(e.lat), lng: parseFloat(e.lng) }))
        .filter((e) => Number.isFinite(e.lat) && Number.isFinite(e.lng)),
    [events]
  );

  // Centre on the results rather than a hard-coded point.
  const center = React.useMemo(() => {
    if (!points.length) return MAP_FALLBACK;
    return {
      lat: points.reduce((s, p) => s + p.lat, 0) / points.length,
      lng: points.reduce((s, p) => s + p.lng, 0) / points.length,
    };
  }, [points]);

  const render = (status) => {
    if (status === Status.FAILURE) {
      return (
        <div className="flex h-[70vh] flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-50 text-rose-500">
            <ExclamationCircleIcon className="h-6 w-6" />
          </span>
          <p className="mt-4 font-semibold text-ink">{t('events.mapError', "La carte n'a pas pu être chargée")}</p>
        </div>
      );
    }
    return <div className="h-[70vh] w-full animate-pulse rounded-2xl bg-slate-100" />;
  };

  if (!process.env.REACT_APP_GOOGLE_KEY) {
    return (
      <div className="flex h-[70vh] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-600">
          <ExclamationCircleIcon className="h-6 w-6" />
        </span>
        <p className="mt-4 font-semibold text-ink">{t('events.mapError', "La carte n'a pas pu être chargée")}</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-100 shadow-card [&_#map]:!h-[70vh]">
      {!points.length ? (
        <div className="flex h-[70vh] items-center justify-center bg-white px-6 text-center text-sm text-ink-muted">
          {t('events.mapNoCoords', 'Aucun résultat géolocalisé à afficher sur la carte.')}
        </div>
      ) : (
        <Wrapper apiKey={process.env.REACT_APP_GOOGLE_KEY} render={render}>
          <Map center={center} zoom={points.length === 1 ? 12 : 6} events={points} />
        </Wrapper>
      )}
    </div>
  );
}

// --- page -------------------------------------------------------------------

export default function Events() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const isSearch = location.pathname === '/search';
  const query = new URLSearchParams(location.search);

  const filters = parseFilters(location.search);
  const activityName = query.get('activityName') || '';
  const miles = query.get('miles') || '';
  const eventType = query.get('eventType') || '';
  const page = parseInt(query.get('page') || '1', 10);

  const [events, setEvents] = React.useState([]);
  const [pages, setPages] = React.useState(0);
  const [total, setTotal] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [searchText, setSearchText] = React.useState(activityName);
  // 'list' | 'map' — the map shows the same filtered results.
  const [view, setView] = React.useState('list');

  // Keep the search input synced if the URL changes (e.g. browser back).
  React.useEffect(() => {
    setSearchText(activityName);
  }, [activityName]);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);

    const params = {
      page,
      type: filters.types.join(','),
      activityName,
      city: filters.city || undefined,
      miles,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      eventType,
      per_page: PER_PAGE,
    };
    Object.keys(params).forEach((k) => {
      if (params[k] === undefined || params[k] === '') delete params[k];
    });

    const req = isSearch ? API.Search(params) : API.Events(params);
    req
      .then((res) => {
        if (!mounted) return;
        setEvents(res.data || []);
        setPages(res.meta?.last_page || 0);
        setTotal(res.meta?.total ?? (res.data ? res.data.length : 0));
      })
      .catch(() => {
        if (!mounted) return;
        setEvents([]);
        setPages(0);
        setTotal(0);
      })
      .finally(() => mounted && setLoading(false));

    document.title = `${API.APP_NAME} - ${isSearch ? 'Search' : 'Events'}`;
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.pathname]);

  // Navigate by rewriting the query string — single source of truth.
  const navigate = (next) => {
    const qs = buildQuery({
      filters: next.filters ?? filters,
      activityName: next.activityName ?? activityName,
      miles,
      eventType,
      page: next.page ?? 1,
    });
    history.push(`${location.pathname}?${qs}`);
  };

  const onApplyFilters = (nextFilters) => navigate({ filters: nextFilters, page: 1 });
  const onChangePage = (n) => {
    navigate({ page: n });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const onSubmitSearch = (e) => {
    e.preventDefault();
    navigate({ activityName: searchText.trim(), page: 1 });
  };
  const clearSearch = () => {
    setSearchText('');
    navigate({ activityName: '', page: 1 });
  };

  return (
    <div className="bg-surface-muted">
      {/* ---------- Page header ---------- */}
      <header className="border-b border-slate-100 bg-surface">
        <div className="mx-auto flex w-full max-w-container flex-col items-center px-4 py-10 text-center sm:px-6 sm:py-14 lg:px-8">
          <h1 className="font-display text-3xl font-bold tracking-tight text-ink sm:text-[40px] sm:leading-tight">
            {isSearch ? t('events.heroTitleSearch') : t('events.heroTitle')}
          </h1>
          <p className="mt-3 max-w-xl text-sm text-ink-muted sm:text-base">
            {t('events.heroSubtitle')}
          </p>

          {/* Search field */}
          <form onSubmit={onSubmitSearch} className="mt-7 w-full max-w-xl">
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-4">
                <MagnifyingGlassIcon className="h-5 w-5 text-ink-subtle" />
              </span>
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t('events.searchPlaceholder')}
                className="w-full rounded-full border border-slate-200 bg-white py-3.5 pl-12 pr-28 text-sm text-ink shadow-card placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 rtl:pl-28 rtl:pr-12"
              />
              {searchText ? (
                <button
                  type="button"
                  onClick={clearSearch}
                  aria-label="Clear"
                  className="absolute inset-y-0 right-24 flex items-center text-ink-subtle hover:text-ink rtl:right-auto rtl:left-24"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              ) : null}
              <button
                type="submit"
                className="absolute inset-y-1.5 right-1.5 flex items-center rounded-full bg-brand-500 px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 rtl:right-auto rtl:left-1.5"
              >
                <MagnifyingGlassIcon className="h-4 w-4 sm:hidden" />
                <span className="hidden sm:inline">{t('events.searchButton')}</span>
              </button>
            </div>
          </form>
        </div>
      </header>

      {/* ---------- Body ---------- */}
      <div className="mx-auto w-full max-w-container px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
          {/* Filters (sidebar desktop / sheet mobile) */}
          <div className="lg:contents">
            <EventsFilters
              value={filters}
              onApply={onApplyFilters}
              resultCount={total}
              loading={loading}
            />
          </div>

          {/* Results */}
          <main>
            {/* Results count */}
            <div className="mb-5 flex items-center justify-between gap-3">
              <p className="text-sm text-ink-muted">
                {loading
                  ? t('events.resultsLoading')
                  : t('events.resultsCount', { count: total ?? 0 })}
              </p>

              {/* List / map toggle. Both views render the SAME filtered
                  `events`, so the sidebar filters drive the map pins too. */}
              <div className="flex shrink-0 items-center gap-1 rounded-full border border-slate-200 bg-white p-1 shadow-card">
                <button
                  type="button"
                  onClick={() => setView('list')}
                  aria-pressed={view === 'list'}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                    view === 'list'
                      ? 'bg-brand-500 text-white'
                      : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  <Squares2X2Icon className="h-4 w-4" />
                  {t('events.listView', 'Liste')}
                </button>
                <button
                  type="button"
                  onClick={() => setView('map')}
                  aria-pressed={view === 'map'}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                    view === 'map'
                      ? 'bg-brand-500 text-white'
                      : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  <MapIcon className="h-4 w-4" />
                  {t('events.mapView', 'Carte')}
                </button>
              </div>
            </div>

            {loading ? (
              view === 'map' ? (
                <div className="h-[70vh] w-full animate-pulse rounded-2xl bg-slate-100" />
              ) : (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <CardSkeleton key={i} />
                  ))}
                </div>
              )
            ) : events.length ? (
              view === 'map' ? (
                <EventsMap events={events} />
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                    {events.map((event) => (
                      <EventCard key={event.id} event={event} width={null} className="w-full" />
                    ))}
                  </div>
                  <Pagination page={page} pages={pages} onChange={onChangePage} />
                </>
              )
            ) : (
              <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-20 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-surface-muted">
                  <MagnifyingGlassIcon className="h-7 w-7 text-ink-subtle" />
                </div>
                <h3 className="mt-5 font-display text-lg font-semibold text-ink">
                  {t('events.emptyTitle')}
                </h3>
                <p className="mt-1.5 max-w-sm text-sm text-ink-muted">{t('events.emptyBody')}</p>
                <button
                  type="button"
                  onClick={() => onApplyFilters({ types: [], city: '', minPrice: '', maxPrice: '' })}
                  className="mt-5 rounded-full bg-brand-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600"
                >
                  {t('events.emptyCta')}
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
